<template>
  <div class="user-management">
    <div class="user-mgmt-actions" style="margin-bottom:16px;display:flex;gap:12px;align-items:center;justify-content:space-between;">
      <div style="display:flex;gap:12px;">
        <router-link to="/admin" class="btn">返回控制台</router-link>
        <router-link to="/" class="btn">返回主页</router-link>
      </div>
      <div class="current-user" style="font-size:0.98rem;color:var(--text-secondary);white-space:nowrap;">当前登录：{{ currentUserName }}</div>
    </div>
    <h2>用户管理</h2>
    <div v-if="flashMessage" :class="['flash-bar', flashType]">{{ flashMessage }}</div>
    <div v-if="loading" class="loading-overlay"><div class="spinner"></div></div>
    <table class="table table-bordered">
      <thead>
        <tr>
          <th>用户名</th>
          <th>邮箱</th>
          <th>角色</th>
          <th>状态</th>
          <th>Memo</th>
          <th>创建时间</th>
          <th>更新时间</th>
          <th>操作</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="user in filteredUsers" :key="String(user.id)">
          <tr>
            <td>{{ user.userName }}</td>
            <td>{{ user.email }}</td>
            <td>
              <input v-model="user.rolesStr" @blur="updateUser(user)" placeholder="如: 1,3" style="width:60px" />
            </td>
            <td>
              <select v-model="user.status" @change="updateUser(user)">
                <option :value="0">未激活</option>
                <option :value="1">正常</option>
                <option :value="2">禁用</option>
              </select>
            </td>
            <td>
              <input v-model="user.memo" @blur="updateUser(user)" style="width:100px" />
            </td>
            <td>{{ formatDate(user.createdAt) }}</td>
            <td>{{ formatDate(user.updatedAt) }}</td>
            <td>
              <div style="display:flex;gap:4px;flex-wrap:nowrap;">
                <button @click="deleteUser(user)">删除</button>
                <button @click="openResetPwdDialog(user)">重置密码</button>
                <button @click="toggleUserNodes(user)">{{ expandedUserId === user.id ? '收起节点' : '收起' }}</button>
                <button @click="openAssignDialog(user)">分配节点</button>
              </div>
            </td>
          </tr>
          <tr v-if="user && user.id && expandedUserId === user.id">
            <td colspan="8">
              <UserNodeTable :userId="user.id" :nodes="getUserNodes(user.id)" :key="userNodeTableKey[user.id] || 0" />
            </td>
          </tr>
        </template>
      </tbody>
    </table>
    <!-- 重置密码弹窗 -->
    <div v-if="showPwdDialog" class="reset-pwd-dialog">
      <div class="reset-pwd-content">
        <h3>重置密码：{{ pwdUser?.userName }}</h3>
        <input v-model="newPassword" type="password" placeholder="新密码" />
        <div style="margin-top:12px;">
          <button @click="doResetPwd" :disabled="!newPassword">确定</button>
          <button @click="closePwdDialog">取消</button>
        </div>
      </div>
    </div>
    <!-- 分配节点弹窗 -->
    <div v-if="showAssignDialog" class="assign-dialog">
      <div class="assign-content">
        <h3>分配节点：{{ assignUser?.userName }}</h3>
        <div style="max-height:300px;overflow:auto;">
          <label v-for="node in allNodes" :key="node.id" style="display:block;">
            <input type="checkbox" :value="node.id" v-model="selectedNodeIds" />
            {{ node.siteName }} ({{ node.id }})
          </label>
        </div>
        <div style="margin-top:12px;">
          <button @click="doAssignNodes">确定</button>
          <button @click="closeAssignDialog">取消</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getUsers, updateUser, deleteUser, resetUserPassword, getUserNodes, getAllNodes, setUserNodes } from '@/services/apiService';
import { useAuthStore } from '@/stores/userStore';
import UserNodeTable from './UserNodeTable.vue';

export default {
  name: 'UserManagement',
  components: { UserNodeTable },
  data() {
    return {
      users: [],
      loading: false,
      flashMessage: '',
      flashType: '',
      showPwdDialog: false,
      pwdUser: null,
      newPassword: '',
      userNodesMap: {},
      expandedUserId: null,
      showAssignDialog: false,
      assignUser: null,
      allNodes: [],
      selectedNodeIds: [],
      userNodeTableKey: {},
      currentUserName: ''
    };
  },
  async mounted() {
    const authStore = useAuthStore();
    this.currentUserName = authStore.username || '未知用户';
    await this.fetchUsers();
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      // 检查日期是否有效
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return date.toLocaleString();
    },
    showFlash(msg, type = 'error') {
      if (!msg) return;
      this.flashMessage = msg;
      this.flashType = type;
      setTimeout(() => { this.flashMessage = ''; }, 10000);
    },
    openResetPwdDialog(user) {
      this.pwdUser = user;
      this.newPassword = '';
      this.showPwdDialog = true;
    },
    closePwdDialog() {
      this.showPwdDialog = false;
      this.pwdUser = null;
      this.newPassword = '';
    },
    async doResetPwd() {
      if (!this.pwdUser || !this.newPassword) return;
      this.loading = true;
      try {
        const authStore = useAuthStore();
        const token = authStore.token;
        await resetUserPassword(this.pwdUser.id, this.newPassword, token,this.pwdUser.userName);
        this.showFlash('密码已重置', 'success');
        this.closePwdDialog();
      } catch (err) {
        this.showFlash(err.statusText || err.message || '重置失败', 'error');
      } finally {
        this.loading = false;
      }
    },
    async fetchUsers() {
      this.loading = true;
      try {
        const authStore = useAuthStore();
        const token = authStore.token;
        const res = await getUsers(token);
        this.users = res.map(u => ({ ...u, rolesStr: (u.roles||[]).join(',') }));
      } catch (err) {
        if (err.status === 401 || (err.statusText && err.statusText.includes('无权限'))) {
          this.showFlash('无权限访问，请重新登录', 'error');
          setTimeout(()=>{ this.$router.replace('/login'); }, 2000);
        } else {
          this.showFlash(err.statusText || err.message || '请求失败', 'error');
        }
      } finally {
        this.loading = false;
      }
    },
    async updateUser(user) {
      this.loading = true;
      try {
        const authStore = useAuthStore();
        const token = authStore.token;
        const roles = user.rolesStr.split(',').map(r=>parseInt(r)).filter(Boolean);
        await updateUser({ id: user.id, roles, status: user.status, memo: user.memo }, token);
        await this.fetchUsers();
      } catch (err) {
        if (err.status === 401 || (err.statusText && err.statusText.includes('无权限'))) {
          this.showFlash('无权限操作，请重新登录', 'error');
          setTimeout(()=>{ this.$router.replace('/login'); }, 2000);
        } else {
          this.showFlash(err.statusText || err.message || '更新失败', 'error');
        }
      } finally {
        this.loading = false;
      }
    },
    async deleteUser(user) {
      if (!confirm('确定要删除该用户吗？')) return;
      this.loading = true;
      try {
        const authStore = useAuthStore();
        const token = authStore.token;
        await deleteUser(user.id, token);
        await this.fetchUsers();
      } catch (err) {
        if (err.status === 401 || (err.statusText && err.statusText.includes('无权限'))) {
          this.showFlash('无权限操作，请重新登录', 'error');
          setTimeout(()=>{ this.$router.replace('/login'); }, 2000);
        } else {
          this.showFlash(err.statusText || err.message || '删除失败', 'error');
        }
      } finally {
        this.loading = false;
      }
    },
    async resetUserPassword(user) {
      if (!confirm('确定要重置该用户的密码吗？')) return;
      this.loading = true;
      try {
        const authStore = useAuthStore();
        const token = authStore.token;
        await resetUserPassword(user.id, token);
        await this.fetchUsers();
      } catch (err) {
        if (err.status === 401 || (err.statusText && err.statusText.includes('无权限'))) {
          this.showFlash('无权限操作，请重新登录', 'error');
          setTimeout(()=>{ this.$router.replace('/login'); }, 2000);
        } else {
          this.showFlash(err.statusText || err.message || '重置密码失败', 'error');
        }
      } finally {
        this.loading = false;
      }
    },
    async toggleUserNodes(user) {
      if (!user || !user.id) return;
      if (this.expandedUserId === user.id) {
        this.expandedUserId = null;
        return;
      }
      this.expandedUserId = user.id;
      if (!this.userNodesMap[user.id]) {
        const authStore = useAuthStore();
        const token = authStore.token;
        const nodes = await getUserNodes(user.id, token);
        this.userNodesMap[user.id] = Array.isArray(nodes) ? nodes : [];
      }
    },
    async openAssignDialog(user) {
      if (!user || !user.id) return;
      this.assignUser = user;
      this.showAssignDialog = true;
      this.selectedNodeIds = [];
      const authStore = useAuthStore();
      const token = authStore.token;
      this.allNodes = await getAllNodes(token);
      // 预选中已分配节点，确保 id 为数字
      const userNodes = this.userNodesMap[user.id] || [];
      this.selectedNodeIds = userNodes.map(n => Number(n.id));
    },
    closeAssignDialog() {
      this.showAssignDialog = false;
      this.assignUser = null;
      this.selectedNodeIds = [];
    },
    async doAssignNodes() {
      if (!this.assignUser || !this.assignUser.id) return;
      this.loading = true;
      try {
        const authStore = useAuthStore();
        const token = authStore.token;
        const nodes = await setUserNodes(this.assignUser.id, this.selectedNodeIds.map(id => Number(id)), token);
        const assignUserId = this.assignUser.id; // 先保存
        this.showFlash('节点分配成功', 'success');
        this.closeAssignDialog();
        // 分配后立即刷新 userNodesMap
        this.userNodesMap[assignUserId] = Array.isArray(nodes)
          ? nodes.filter(n => n && n.id !== undefined && n.id !== null && n.id !== '')
          : [];
        // 分配后自动展开节点子表
        this.expandedUserId = assignUserId;
        if (this.expandedUserId) {
          this.userNodeTableKey[this.expandedUserId] = (this.userNodeTableKey[this.expandedUserId] || 0) + 1;
        }
      } catch (err) {
        this.showFlash(err.statusText || err.message || '分配失败', 'error');
      } finally {
        this.loading = false;
      }
    },
    getUserNodes(userId) {
      const arr = this.userNodesMap[userId];
      return Array.isArray(arr)
        ? arr.filter(n => n && n.id !== undefined && n.id !== null && n.id !== '')
        : [];
    }
  },
  computed: {
    filteredUsers() {
      return this.users.filter(user => user && user.id);
    }
  }
};
</script>

<style scoped>
.user-management { padding: 24px; }
.loading-overlay { position: fixed; top:0; left:0; right:0; bottom:0; background:rgba(0,0,0,0.1); display:flex; align-items:center; justify-content:center; z-index:1000; }
.spinner { width:40px; height:40px; border:4px solid #eee; border-top:4px solid #3b82f6; border-radius:50%; animation:spin 1s linear infinite; }
@keyframes spin { 0%{transform:rotate(0);} 100%{transform:rotate(360deg);} }
.btn {
  display: inline-block;
  padding: 8px 18px;
  background: var(--accent-color, #3b82f6);
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 0.95rem;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.2s;
}
.btn:hover {
  background: var(--text-primary, #2563eb);
}
.flash-bar {
  position: fixed;
  top: 32px;
  left: 50%;
  transform: translateX(-50%);
  min-width: 200px;
  max-width: 90vw;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  color: #fff;
  z-index: 2000;
  box-shadow: 0 2px 12px rgba(0,0,0,0.15);
  animation: fadeInOut 2s;
}
.flash-bar.error { background: #ef4444; }
.flash-bar.success { background: #10b981; }
@keyframes fadeInOut {
  0% { opacity: 0; transform: translate(-50%, 0.5rem); }
  10% { opacity: 1; transform: translate(-50%, 0); }
  90% { opacity: 1; transform: translate(-50%, 0); }
  100% { opacity: 0; transform: translate(-50%, -0.5rem); }
}
.reset-pwd-dialog {
  position: fixed; top:0; left:0; right:0; bottom:0;
  background: rgba(0,0,0,0.2); display: flex; align-items: center; justify-content: center; z-index: 2001;
}
.reset-pwd-content {
  background: #fff; color: #222; padding: 32px 24px; border-radius: 10px; min-width: 320px; box-shadow: 0 4px 24px rgba(0,0,0,0.15);
}
.reset-pwd-content input { width: 100%; padding: 8px; margin-top: 8px; }
.reset-pwd-content button { margin-right: 12px; }
.assign-dialog { position: fixed; top:0; left:0; right:0; bottom:0; background: rgba(0,0,0,0.2); display: flex; align-items: center; justify-content: center; z-index: 2001; }
.assign-content { background: #fff; color: #222; padding: 32px 24px; border-radius: 10px; min-width: 320px; box-shadow: 0 4px 24px rgba(0,0,0,0.15); }
</style> 